/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Input, PopoverBody, PopoverHeader, UncontrolledPopover, UncontrolledTooltip } from 'reactstrap';
import { Country, State, City } from 'country-state-city';

import { store } from 'slices/persister';
import EllipsisToolTip from './Tooltip/Tooltip';
import view from '../../assets/images/commons/view.svg';
import deleteImage from '../../assets/images/commons/delete.svg';
import mines from '../../assets/images/commons/mines.svg';
import addMine from '../../assets/images/commons/addMine.svg';
import edit from '../../assets/images/commons/edit.svg';
import Comment from '../../assets/images/commons/Comment.svg';
import { ENCRYPTION_CONSTANTS } from 'Components/constants/appConstants';
import CryptoJS from 'crypto-js';
import { match } from 'path-to-regexp';
import { routePatterns } from 'helpers/url_helper';
import { Vector as VectorSource } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import { Style, Circle, Fill, Stroke } from "ol/style";
import { Vector as VectorLayer } from "ol/layer";
import { transform } from "ol/proj";
import { getLength } from 'ol/sphere.js';

const getDate = (date: string | number | Date, dateWithoutTime?: boolean | undefined) => {
    const sDate = new Date();
    if (!date) {
        date = new Date(sDate);
    }
    date = new Date(date);
    let DATE_OPTIONS: any = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    if (dateWithoutTime) {
        DATE_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };
    }
    return date.toLocaleDateString('en-US', DATE_OPTIONS);
};

export const getTime = (d: string | number | Date) => {
    const date = new Date(d);
    return date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
};

export const transformDate = (dateString: any, isLastTime?: boolean) => {
    // Parse the date string into a Date object
    const date = new Date(dateString);

    // Extract the components
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based
    const day = date.getDate().toString().padStart(2, '0');
    // const hours = date.getHours().toString().padStart(2, '0');
    // const minutes = date.getMinutes().toString().padStart(2, '0');
    // const seconds = date.getSeconds().toString().padStart(2, '0');

    // Format the date

    // last T23:59:59.999+00:00
    const formattedDate = isLastTime
        ? `${year}-${month}-${day}T23:59:59.999+00:00`
        : // : `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000+00:00`;
        `${year}-${month}-${day}T00:00:00.000+00:00`;
    return formattedDate;
};

// yyyy-mm-dd format
const formatDateToYYYYMMDD = (date: any) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
};

// dd-mm-yyyy format
const formatDateToDDMMMYYYY = (date: any) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0'); // Get day with leading zero if needed
    const month = d.toLocaleString('en-US', { month: 'short' }); // Get short month name
    const year = d.getFullYear(); // Get year
    return `${day} ${month}, ${year}`;
};

// modified date format as per the requirement
export const convertDateToFullString = (dateString: string): string => {
    // Parse the date string into a Date object
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is zero-based in Date object

    // Extract components
    const dayOfMonth = date.getDate();
    const monthName = date.toLocaleString('default', { month: 'long' });
    const yearValue = date.getFullYear();

    // Function to get the day suffix
    const getDaySuffix = (day: number): string => {
        if (day >= 11 && day <= 13) return 'th';
        switch (day % 10) {
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    };

    // Format the date
    return `${dayOfMonth}${getDaySuffix(dayOfMonth)} ${monthName} ${yearValue}`;
};

const convertBytesTo = (bytes: number) => {
    const decimals = 0;
    if (!+bytes) return '0 Bytes';

    const k = 1023;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const convertDataToBytes = (size: string) => {
    const unit = size.substring(size.length - 2, size.length);
    const amount = parseInt(size.substring(0, size.length - 2));
    let valueInBytes = 0;
    switch (unit?.toUpperCase()) {
        case 'KB':
            valueInBytes = amount * 1024;
            break;
        case 'MB':
            valueInBytes = amount * 1024 * 1024;
            break;
        case 'GB':
            valueInBytes = amount * 1024 * 1024 * 1024;
            break;
        case 'TB':
            valueInBytes = amount * 1024 * 1024 * 1024 * 1024;
            break;
        default:
            break;
    }
    return valueInBytes;
};

const convertSecondsTo = (time: number) => {
    const seconds = Math.floor((time / 1000) % 60);
    const minutes = Math.floor((time / 1000 / 60) % 60);
    const hours = Math.floor((time / 1000 / 60 / 60) % 60);
    return { hours, minutes, seconds };
};

const getDateOnly = (date: Date) => {
    return getDate(date, true);
};

const createSelectOption: any = (arr: any, data: any) => {
    let options = arr?.map((item: any) => {
        if (data === 'startDate') {
            let date = getDateOnly(new Date(item[data]));
            return { label: date, value: date };
        } else if (data === 'projectName' || data === 'surveyName') {
            return { label: item[data], value: item[data], id: item['id'] };
        } else if (data === 'dateOfSurvey') {
            let val = getDateOnly(item[data]);
            return { label: val, value: val };
        } else {
            return { label: item[data], value: item[data] };
        }
    });
    return options;
};

const getDateUTCFormat = (param: string | number | Date) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateObj = new Date(param);
    const month = monthNames[dateObj.getMonth()];
    const day = String(dateObj.getDate() - 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return month + '\n' + day + ', ' + year;
};

const getUTCTime = (date: string | null | any) => {
    return date === '' || date === null ? date : new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const uniqBy = (arr: any[], predicate: string | number) => {
    const cb = typeof predicate === 'function' ? predicate : (o: { [x: string]: any }) => o[predicate];
    return [
        ...arr
            .reduce((map: { has: (arg0: any) => any; set: (arg0: any, arg1: any) => any }, item: null | undefined) => {
                const key = item === null || item === undefined ? item : cb(item);
                map.has(key) || map.set(key, item);
                return map;
            }, new Map())
            .values()
    ];
};

const uniqueID = (length: number) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

const getMonth = (val: string | number | any) => {
    return val > 9 ? '-' + val : '-0' + val;
};

const getDateVal = (val: string | number | any) => {
    return val > 9 ? val : '0' + val;
};

const convertTextCase = (name: string, param?: boolean | undefined) => {
    let result;
    if (param) result = name?.charAt(0)?.toUpperCase() + name?.substr(1)?.toLowerCase();
    else result = name?.charAt(0)?.toUpperCase() + name?.substr(1);
    return result;
};

export const convertHMSTOMS = (time: string) => {
    const hms: any = time.split(':');
    return hms[0] * 60 * 60 * 1000 + hms[1] * 60 * 1000 + hms[2] * 1000;
};

const getUTCGteLteFromDate = (dateVal: string | number | Date | any) => {
    const time = new Date(dateVal);
    const previousDay = new Date(dateVal - 1);
    const gte =
        previousDay.getFullYear() + getMonth(previousDay.getMonth() + 1) + '-' + getDateVal(previousDay.getDate()) + 'T18:31:00.000Z';
    const lte = time.getFullYear() + getMonth(time.getMonth() + 1) + '-' + getDateVal(time.getDate()) + 'T18:29:00.000Z';
    return { gte: gte, lte: lte };
};

const profileTypeLabel = (policyType: string) => {
    let label;
    if (policyType === 'FM') label = 'Fully Managed Device';
    else if (policyType === 'WP') label = 'Work Profile';
    else if (policyType === 'DD') label = 'Dedicated Device';
    else if (policyType === 'SA') label = 'Single App';
    else if (policyType === 'KL') label = 'Kiosk Launcher';
    else if (policyType === 'TL') label = 'Tectoro Launcher';
    return label;
};

const convertUTCtoIST = (date: string | number | Date | null | undefined, dateOnly?: any) => {
    if (date !== null && date !== '' && date !== undefined) {
        const dateUTC = new Date(date);
        const dateUTCTime = dateUTC.getTime();
        const dateIST: any = new Date(dateUTCTime);
        /*
         * dateIST.setHours(dateIST.getHours() + 5); // date shifting for IST timezone (+5 hours and 30 minutes)
         * dateIST.setMinutes(dateIST.getMinutes() + 30);
         */
        return dateOnly ? getDateOnly(dateIST) : getDate(dateIST);
    } else {
        return dateOnly ? getDateOnly(new Date()) : getDate(new Date());
    }
};

const convertScientificToUTC = (
    timestamp: string | number | null | undefined,
    dateOnly?: boolean,
    timeOnly?: boolean,
    is24HourFormat?: boolean
): string => {
    if (!timestamp) return 'Invalid Date';

    const timeInMs = Number(timestamp) * 1000;
    if (isNaN(timeInMs)) return 'Invalid Date';

    const dateUTC = new Date(timeInMs);

    const day = dateUTC.getUTCDate();
    const month = dateUTC.getUTCMonth() + 1;
    const year = dateUTC.getUTCFullYear();
    const hours = dateUTC.getUTCHours();
    const minutes = dateUTC.getUTCMinutes();
    const seconds = dateUTC.getUTCSeconds();

    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    const formattedHours = hours.toString().padStart(2, '0');

    const dateStr = `${day}-${month}-${year}`;

    let timeStr = '';
    if (is24HourFormat) {
        timeStr = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    } else {
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
        timeStr = `${formattedHours}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
    }

    if (timeOnly) return timeStr;
    if (dateOnly) return dateStr;

    return `${dateStr}, ${timeStr}`;
};

const add530ToTime = (time: string | number | Date) => {
    let formattedDate = '—';
    if (time) {
        const date = new Date(time);
        date.setHours(date.getHours() + 5);
        date.setMinutes(date.getMinutes() + 30);
        formattedDate = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        });
    }
    return formattedDate;
};

export const sortStringNumerics = (arr: any[], key: string | number) => {
    return arr.sort((a: { [x: string]: string }, b: { [x: string]: string }) => {
        const versionA = a[key].split('.').map(Number);
        const versionB = b[key].split('.').map(Number);

        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const numA = versionA[i] || 0;
            const numB = versionB[i] || 0;

            if (numA !== numB) {
                return numB - numA;
            }
        }

        return 0;
    });
};

const convertDate = (str: string | number | Date, key: string | undefined) => {
    const date = new Date(str),
        mnth = ('0' + (date.getMonth() + 1)).slice(-2),
        day = ('0' + date.getDate()).slice(-2);
    return [date.getFullYear(), mnth, day].join(key);
};

const convertDateToUTC = (str: any, isLastTime?: boolean) => {
    if (isLastTime) {
        const date = new Date(str);
        date.setUTCHours(23, 59, 59, 999);
        return date.toISOString();
    }
    const before = new Date(str?.getTime() + 1 * 24 * 60 * 60 * 1000);
    before?.setUTCHours(0, 0, 0, 0);
    const formattedDate = before?.toISOString();
    return formattedDate;
};

/*
 * const convertDateToUTC = (str: any) => {
 * 	const date = new Date(str);
 * 	date.setUTCHours(0, 0, 0, 0);
 * 	const formattedDate = date.toISOString();
 * 	return formattedDate;
 *   };
 */

export const compareDates = (lrt: string | number | Date, datePc2: string | number | Date) => {
    let comparision = true;
    const d1 = new Date(lrt);
    const d2 = new Date(datePc2);
    if (!lrt && datePc2 && d2.getFullYear() > 2021) {
        comparision = true;
    } else if (lrt && (!datePc2 || d2.getFullYear() < 2021)) {
        comparision = false;
    } else {
        comparision = d1 < d2;
    }
    return comparision;
};

export const getFormTypeAndRecordIDForTickets = (path: string) => {
    let formType;
    let recordID = '';
    // let queryType;
    const pathArr: any = path.split('/');
    const actionExist = path.search('/takeAction') !== -1;
    const viewExist = path.search('/viewAction') !== -1;
    recordID = pathArr[pathArr.length - 1];
    if (actionExist) {
        formType = 'edit';
    } else if (viewExist) {
        formType = 'view';
    }
    return { formType: formType, recordID: recordID };
};

export const getFormTypeAndRecordId = (path: any, extraParam?: any) => {
    let formType;
    let recordID = '';
    let queryType;
    const createExist = path.search('/add') !== -1;
    const viewExist = path.search('/view') !== -1;
    const editExist = path.search('/edit') !== -1;
    const allExist = path.search('/all') !== -1;
    const takeActionExist = path.search('/takeAction') !== -1;
    const enrollmentTokenExist = path.search('/enrollmenttoken') !== -1;
    const checkerExist = path.search('/checker') !== '-1';
    if (editExist) {
        formType = 'edit';
        recordID = path.split('/').reverse()[extraParam ? 1 : 0];
        if (extraParam) queryType = path.split('/').reverse()[0];
    } else if (viewExist) {
        formType = 'view';
        recordID = path.split('/').reverse()[extraParam ? 1 : 0];
        if (extraParam) queryType = path.split('/').reverse()[0];
    } else if (createExist) {
        formType = 'add';
        if (extraParam) queryType = path.split('/').reverse()[0];
    } else if (allExist) {
        formType = 'all';
    } else if (takeActionExist) {
        formType = 'takeAction';
        recordID = path.split('/').reverse()[extraParam ? 1 : 0];
        if (extraParam) queryType = path.split('/').reverse()[0];
    } else if (enrollmentTokenExist) {
        recordID = path.split('/').reverse()[extraParam ? 1 : 0];
        if (extraParam) queryType = path.split('/').reverse()[0];
    } else if (checkerExist) {
        recordID = path.split('/').reverse()[extraParam ? 1 : 0];
        if (extraParam) queryType = path.split('/').reverse()[0];
        formType = 'viewChecker';
    }
    return { formType: formType, recordID: recordID, queryType: queryType };
};

const baseURL = () => {
    return window.location.origin?.split(':')?.includes('//localhost') ? process.env.REACT_APP_URL : window.location.origin;
};

/*
 * const TenantConfig = () => {
 *     return Store.getState()?.TenantConfig?.tenantConfig;
 * };
 */

const AuthUser = (): string | null | any => {
    const authUser = store.getState()?.Login?.user;
    return authUser && Object.keys(authUser)?.length > 0 ? authUser : null;
};

/*
 * const Enterprise = () => {
 *     return Store.getState()?.Commons?.enterpriseAccount;
 * };
 */

export const isUserLoggedOut = (): boolean => {
    return store.getState().Login?.isUserLogout;
};

export function DownloadFile(Url: string, fileNameToDownload: string) {
    fetch(baseURL() + '/api/' + Url).then((response) => {
        response.blob().then((blob) => {
            const fileURL = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = fileURL;
            a.download = fileNameToDownload;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        });
    });
}

const Tooltip = (id: any, label: any, placement?: any) => {
    return (
        <UncontrolledTooltip placement="bottom" target={id}>
            {label}
        </UncontrolledTooltip>
    );
};

// tooltip for tools on map
const Tooltips = (id: any, label: any, placement: any) => {
    return (
        <UncontrolledTooltip placement={placement ? placement : 'bottom'} target={id}>
            {label}
        </UncontrolledTooltip>
    );
};

export const ellipsisToolTip = (label: any) => {
    return <EllipsisToolTip options={{ effect: 'solid', place: 'bottom' }}>{label}</EllipsisToolTip>;
};

export function secondsToHms(d: number) {
    d = Number(d);
    const h = Math.floor(d / 3600);
    const m = Math.floor((d % 3600) / 60);
    const s = Math.floor((d % 3600) % 60);

    const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : '';
    const mDisplay = m > 0 ? m + (m === 1 ? ' minute, ' : ' minutes, ') : '';
    const sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : '';
    return hDisplay + mDisplay + sDisplay;
}

/*
 * export const changeClasses = (configData) => {
 *     if (configData) {
 *         if (configData.COMAPNY_PRIMARY_COLOR)
 *             configData.COMAPNY_PRIMARY_COLOR = configData.COMAPNY_PRIMARY_COLOR?.startsWith('rgba')
 *                 ? configData.COMAPNY_PRIMARY_COLOR
 *                 : hexToRgba(configData.COMAPNY_PRIMARY_COLOR);
 *         if (configData.COMAPNY_SECONDARY_COLOR)
 *             configData.COMAPNY_SECONDARY_COLOR = configData.COMAPNY_SECONDARY_COLOR?.startsWith('rgba')
 *                 ? configData.COMAPNY_SECONDARY_COLOR
 *                 : hexToRgba(configData.COMAPNY_SECONDARY_COLOR);
 *         if (configData.COMAPNY_SUCCESS_COLOR)
 *             configData.COMAPNY_SUCCESS_COLOR = configData.COMAPNY_SUCCESS_COLOR?.startsWith('rgba')
 *                 ? configData.COMAPNY_SUCCESS_COLOR
 *                 : hexToRgba(configData.COMAPNY_SUCCESS_COLOR);
 *     }
 *     var style = document.createElement('style');
 *     var style1 = document.createElement('style');
 *     var style2 = document.createElement('style');
 *     var style3 = document.createElement('style');
 *     var style4 = document.createElement('style');
 *     var style5 = document.createElement('style');
 *     var style6 = document.createElement('style');
 *     var style7 = document.createElement('style');
 *     var style8 = document.createElement('style');
 *     var style9 = document.createElement('style');
 *     var style10 = document.createElement('style');
 *     var style11 = document.createElement('style');
 *     var style12 = document.createElement('style');
 */

/*
 *     style.type = 'text/css';
 *     style1.type = 'text/css';
 *     style2.type = 'text/css';
 *     style3.type = 'text/css';
 *     style4.type = 'text/css';
 *     style5.type = 'text/css';
 *     style6.type = 'text/css';
 *     style7.type = 'text/css';
 *     style8.type = 'text/css';
 *     style9.type = 'text/css';
 *     style10.type = 'text/css';
 *     style11.type = 'text/css';
 *     style12.type = 'text/css';
 */

/*
 *     style.innerHTML = `.link-primary { color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; }`;
 *     style1.innerHTML = `.text-primary {color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; }`;
 *     style2.innerHTML = `.bg-primary {background-color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; }`;
 *     style3.innerHTML = `.link-secondary: {color: ${
 *         configData?.COMAPNY_SECONDARY_COLOR ? configData?.COMAPNY_SECONDARY_COLOR : 'rgba(53, 119, 241, 1)'
 *     } !important; }`;
 *     style4.innerHTML = `.text-secondary: {color: ${
 *         configData?.COMAPNY_SECONDARY_COLOR ? configData?.COMAPNY_SECONDARY_COLOR : 'rgba(53, 119, 241, 1)'
 *     } !important; }`;
 *     style5.innerHTML = `.bg-secondary: {background-color:${
 *         configData?.COMAPNY_SECONDARY_COLOR ? configData?.COMAPNY_SECONDARY_COLOR : 'rgba(53, 119, 241, 1)'
 *     } !important;}`;
 *     style6.innerHTML = `.link-success: {color: ${
 *         configData?.COMAPNY_SUCCESS_COLOR ? configData?.COMAPNY_SUCCESS_COLOR : 'rgba(10, 179, 156, 1)'
 *     } !important; }`;
 *     style7.innerHTML = `.text-success: {color: ${
 *         configData?.COMAPNY_SUCCESS_COLOR ? configData?.COMAPNY_SUCCESS_COLOR : 'rgba(10, 179, 156, 1)'
 *     } !important; }`;
 *     style8.innerHTML = `.bg-success: {background-color:${
 *         configData?.COMAPNY_SUCCESS_COLOR ? configData?.COMAPNY_SUCCESS_COLOR : 'rgba(10, 179, 156, 1)'
 *     } !important;}`;
 *     style9.innerHTML = `.badge-soft-success: {color: ${
 *         configData?.COMAPNY_SUCCESS_COLOR ? configData?.COMAPNY_SUCCESS_COLOR : 'rgba(10, 179, 156, 1)'
 *     } !important; background-color: rgba(${
 *         configData?.COMAPNY_SUCCESS_COLOR ? configData?.COMAPNY_SUCCESS_COLOR : 'rgba(10, 179, 156, 1)'
 *     }, 0.1) !important; }`;
 *     style10.innerHTML = `.badge-soft-secondary: {color: ${
 *         configData?.COMAPNY_SECONDARY_COLOR ? configData?.COMAPNY_SECONDARY_COLOR : 'rgba(53, 119, 241, 1)'
 *     } !important; background-color: rgba(${configData?.COMAPNY_SECONDARY_COLOR}, 0.1) !important; }`;
 *     style11.innerHTML = `.badge-soft-primary: {color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; background-color: rgba(${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     }, 0.1) !important; }`;
 *     style12.innerHTML = `#page-topbar: {background-color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; border-color: ${
 *         configData?.COMAPNY_PRIMARY_COLOR ? configData?.COMAPNY_PRIMARY_COLOR : 'rgba(64, 81, 137, 1)'
 *     } !important; }`;
 */

/*
 *     document.getElementsByTagName('head')[0].appendChild(style);
 *     document.getElementsByTagName('head')[0].appendChild(style1);
 *     document.getElementsByTagName('head')[0].appendChild(style2);
 *     document.getElementsByTagName('head')[0].appendChild(style3);
 *     document.getElementsByTagName('head')[0].appendChild(style4);
 *     document.getElementsByTagName('head')[0].appendChild(style5);
 *     document.getElementsByTagName('head')[0].appendChild(style6);
 *     document.getElementsByTagName('head')[0].appendChild(style7);
 *     document.getElementsByTagName('head')[0].appendChild(style8);
 *     document.getElementsByTagName('head')[0].appendChild(style9);
 *     document.getElementsByTagName('head')[0].appendChild(style10);
 *     document.getElementsByTagName('head')[0].appendChild(style11);
 *     document.getElementsByTagName('head')[0].appendChild(style12);
 * };
 */

export function diffTwoDatesHrsMintsSeconds(date: string | number | Date, extra: string | number | Date) {
    const date1: any = new Date(date);
    const date2: any = extra ? new Date(extra) : new Date();
    const timeDiffInMs = Math.abs(date2 - date1);
    let seconds: any = Math.floor((timeDiffInMs / 1000) % 60);
    let minutes: any = Math.floor((timeDiffInMs / 1000 / 60) % 60);
    let hours: any = Math.floor((timeDiffInMs / 1000 / 60 / 60) % 24);
    const days = Math.floor((timeDiffInMs / 1000 / 60 / 60 / 24) % 7);
    const weeks = Math.floor(timeDiffInMs / 1000 / 60 / 60 / 24 / 7);
    const totalHours = Math.floor(timeDiffInMs / 1000 / 60 / 60);
    const totalMinutes = Math.floor(timeDiffInMs / 1000 / 60);
    const totalSeconds = Math.floor(timeDiffInMs / 1000);
    const totalDays = Math.floor(timeDiffInMs / 1000 / 60 / 60 / 24);
    const totalWeeks = Math.floor(timeDiffInMs / 1000 / 60 / 60 / 24 / 7);

    hours = hours < 10 ? '0' + hours : hours;
    minutes = minutes < 10 ? '0' + minutes : minutes;
    seconds = seconds < 10 ? '0' + seconds : seconds;
    return {
        hours: hours,
        minutes: minutes,
        seconds: seconds,
        days: days,
        weeks: weeks,
        totalWeeks: totalWeeks,
        totalDays: totalDays,
        totalHours: totalHours,
        totalMinutes: totalMinutes,
        totalSeconds: totalSeconds
    };
}

const IsAuthorized = (props: any) => {
    const userString = AuthUser();
    const user: any = userString;
    if (props.privRequired) {
        if (Array.isArray(props.privRequired)) {
            const privilegeKeys = props.privRequired;
            const userString = AuthUser();
            const user: any = userString;
            if (props.privRequired && props.privRequired.length > 0) {
                const findPriv = privilegeKeys.some((key: any) => {
                    return user?.userProfile?.roles?.[0]?.privileges?.includes(key?.toUpperCase());
                });
                if (findPriv) return props.yes();
                else return props.no();
            } else return props.yes();
        } else {
            const findPriv = user?.userProfile?.roles?.[0].privileges?.includes(props.privRequired);
            if (findPriv) return props.yes();
            else return props.no();
        }
    } else return props.yes();
};

IsAuthorized.defaultProps = {
    yes: () => null,
    no: () => null
};

export const IsAuthorizedCondition = (props: any) => {
    const userString = AuthUser();
    const user = userString ? JSON.parse(userString) : '';
    if (user?.data?.[props.key] === props.value || user?.data?.[props.key]?.includes(props.value)) {
        return props.yes();
    }
    return props.no();
};

IsAuthorizedCondition.defaultProps = {
    yes: () => null,
    no: () => null
};

const PrivCheck = (props: any) => {
    const userString = AuthUser();
    const user = userString ? JSON.parse(userString) : {};
    if (props.reader || props.editor) {
        const findPriv = user?.data?.privileges?.includes(props.editor) || user?.data?.privileges?.includes(props.reader);

        if (findPriv) return props.yes();
        else return props.no();
    } else return props.yes();
};

PrivCheck.defaultProps = {
    yes: () => null,
    no: () => null
};

const sortBy = (arr: any[], key: string | number) => {
    return arr.sort((a: { [x: string]: number }, b: { [x: string]: number }) => (a[key] < b[key] ? -1 : b[key] < a[key] ? 1 : 0));
};

const sortByAsc = (arr: any[], key: string | number) => {
    return arr.sort((a: { [x: string]: number }, b: { [x: string]: number }) => (a[key] < b[key] ? 1 : b[key] < a[key] ? -1 : 0));
};

const noCaseSensitiveSortBy: any = (arr: unknown[] | any[], key: string | number) => {
    return arr.sort((a: { [x: string]: { toLowerCase: () => number } }, b: { [x: string]: { toLowerCase: () => number } }) =>
        a[key]?.toLowerCase() < b[key]?.toLowerCase() ? -1 : b[key]?.toLowerCase() < a[key]?.toLowerCase() ? 1 : 0
    );
};

export const sortStringsNoKey = (arr: any[]) => {
    return arr.sort((a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()));
};

export const guid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const getUniqueValues = (array: any[]) => {
    const arr: any[] = [];
    array.forEach((obj: any) => {
        const contains = arr.includes(obj);
        if (!contains) arr.push(obj);
    });
    return arr;
};

const hexCharacters = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 'A', 'B', 'C', 'D', 'E', 'F'];

function getCharacter(index: number) {
    return hexCharacters[index];
}
export function getRandomColor() {
    let hexColorRep = '#';
    for (let index = 0; index < 6; index++) {
        const randomPosition = Math.floor(Math.random() * hexCharacters.length);
        hexColorRep += getCharacter(randomPosition);
    }
    return hexColorRep;
}

export const viewChange = (displayType: any, handleDisaplyChange: any) => {
    return (
        <>
            <button
                type="button"
                onClick={() => handleDisaplyChange('grid')}
                className={`me-2 ps-2 pe-2 pt-1 pb-1 btn ${displayType === 'grid' ? 'btn-primary' : 'btn-soft-primary waves-effect waves-light'
                    }`}
            >
                <span className="d-flex align-items-center fs-18">
                    <i className="ri-grid-fill"></i>
                </span>
            </button>
            <button
                type="button"
                onClick={() => handleDisaplyChange('table')}
                className={`me-2 ps-2 pe-2 pt-1 pb-1 btn ${displayType === 'table' ? 'btn-primary' : 'btn-soft-primary waves-effect waves-light'
                    }`}
            >
                <span className="d-flex align-items-center fs-18">
                    <i className="ri-menu-fill"></i>
                </span>
            </button>
        </>
    );
};

export const getRandomKey = () => {
    return CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex);
};

// export const generateKey = (salt:any, passPhrase:string, aesKeySize:any, aesIterationCount:any) => {
//     let keySize = aesKeySize / 32;
//     let iterationCount = aesIterationCount;
//     var key = CryptoJS.PBKDF2(passPhrase, CryptoJS.enc.Hex.parse(salt), {
//         keySize: keySize,
//         hasher: CryptoJS.algo.SHA256.create(),
//         iterations: iterationCount
//     });
//     return key;
// };

const generateKey = (salt: string, passPhrase: string, aesKeySize: number, aesIterationCount: number): CryptoJS.lib.WordArray => {
    const keySize = aesKeySize / 32;
    const iterationCount = aesIterationCount;

    const key = CryptoJS.PBKDF2(passPhrase, CryptoJS.enc.Hex.parse(salt), {
        keySize: keySize,
        hasher: CryptoJS.algo.SHA256 as any,
        iterations: iterationCount
    });

    return key;
};

export const encrypt = (plainText: any) => {
    let iv = getRandomKey();
    let salt = getRandomKey();
    var key = generateKey(salt, ENCRYPTION_CONSTANTS?.PASSPHRASE, ENCRYPTION_CONSTANTS?.KEYSIZE, ENCRYPTION_CONSTANTS?.ITERATION_COUNT);
    var encrypted = CryptoJS.AES.encrypt(JSON.stringify(plainText), key, { iv: CryptoJS.enc.Hex.parse(iv) });
    var encodedCiphertext = encrypted.ciphertext.toString();
    return `${iv}::${salt}::${encodedCiphertext}`;
};

// export const decrypt = (encryptedData:any) => {
//     const [iv, salt, encodedCiphertext] = encryptedData.split('::');
//     const key = generateKey(salt, ENCRYPTION_CONSTANTS?.PASSPHRASE, ENCRYPTION_CONSTANTS?.KEYSIZE, ENCRYPTION_CONSTANTS?.ITERATION_COUNT);
//     const ciphertext = CryptoJS.enc.Hex.parse(encodedCiphertext);
//     const decrypted = CryptoJS.AES.decrypt({ ciphertext }, key, { iv: CryptoJS.enc.Hex.parse(iv) });
//     return decrypted.toString(CryptoJS.enc.Utf8);
// };

export const decrypt = (encryptedData: string): string => {
    const [iv, salt, encodedCiphertext] = encryptedData.split('::');
    const key = generateKey(salt, ENCRYPTION_CONSTANTS?.PASSPHRASE, ENCRYPTION_CONSTANTS?.KEYSIZE, ENCRYPTION_CONSTANTS?.ITERATION_COUNT);
    const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Hex.parse(encodedCiphertext)
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
        iv: CryptoJS.enc.Hex.parse(iv)
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
};

const text = {
    value: 'SecretMessage123',
    label: 'jay'
};
const encrypted = encrypt(text);
const decrypted = decrypt(encrypted);

// Upload File Validation
export const fileValidation = (type: string, extension: any, validateFile: { [x: string]: any[] }) => {
    type = type?.toUpperCase();
    let field = '';
    switch (type) {
        case 'IMAGE':
            field = 'wallpaper';
            break;
        case 'VIDEO':
            field = 'video';
            break;
        case 'AUDIO':
            field = 'audio';
            break;
        case 'DOCUMENT':
            field = 'documents';
            break;
        case 'BULK_ACTION':
            field = 'bulkAction';
            break;
        case 'APPS':
            field = 'apps';
            break;
        default:
            field = 'all';
    }
    return {
        valid: validateFile[field]?.includes(extension),
        message: validateFile[field]?.length > 1 ? validateFile[field]?.join(' / ') : validateFile[field]?.[0]
    };
};

const ellipsisWithToolTipId = (id: any, val: any) => {
    return (
        <EllipsisToolTip options={{ effect: 'solid', place: 'bottom' }} target={id}>
            {val}
        </EllipsisToolTip>
    );
};

const ellipsisWithToolTip = (val: any) => {
    return <EllipsisToolTip options={{ effect: 'solid', place: 'bottom' }}>{val}</EllipsisToolTip>;
};

export const trimValues = (values: { [x: string]: any }, schema: any[]) => {
    let flag: any = {};
    Object.keys(values).reduce((acc: any, key: any) => {
        acc[key] = typeof values[key] === 'string' ? values[key]?.trim() : values[key];
        if (Object.keys(acc)?.length > 0) {
            Object.keys(acc)?.forEach(() => {
                // schema?.forEach((obj: { value: string; inputType: string; label: any }) => {
                schema?.forEach((obj: { value: string; inputType: string; label: any; mandatory: boolean }) => {
                    if (obj?.value === key) {
                        // if (obj?.inputType === 'input') {
                        if (acc[key] === '' && obj?.mandatory) {
                            if (acc[key] === '') {
                                flag = { ...flag, [key]: `${obj?.label} ${'is required'}` };
                            } else {
                                if (flag[key]) {
                                    delete flag[key];
                                }
                            }
                        } else {
                            delete flag[key];
                        }
                    } else if (acc[key] !== '') {
                        delete flag[key];
                    }
                });
            });
        }
        if (typeof values[key] !== 'string') {
            delete flag[key];
        }
        return flag;
    }, {});

    return flag;
};

const handleRegexTypeMsg = (field: any = {}) => {
    const regexNames: any = {
        alphanumericspecial: 'alphanumeric special characters',
        alphaNumeric: 'alphaNumeric',
        alphabets: 'alphabets',
        alphabetsandspecialchars: 'alphabets and special characters',
        alphabetsandspecs: 'alphabets and special characters',
        floatdecimal: 'decimals',
        percentage: 'in between 1-100%',
        numbers: 'numbers'
    };
    let regexName = '';
    if (field && Object.keys(field)?.length > 0) {
        Object.keys(field).forEach((key) => {
            if (regexNames[key]) {
                regexName = regexNames[key];
            }
        });
    }
    return regexName;
};

export const getFormattedDate = (date: string, addToMonth: boolean) => {
    const dateVal = new Date(date);
    const onlyDate = dateVal.getDate();
    const month = addToMonth ? dateVal.getMonth() + 1 : dateVal.getMonth();
    const year = dateVal.getFullYear();
    return (onlyDate < 10 ? '0' + onlyDate : onlyDate) + '-' + (month < 10 ? '0' + month : month) + '-' + year;
};

export const prepareReqBodyDate = (date: any, type: string) => {
    if (type === 'edit') {
        date = date?.split(':');
        const newDate = new Date();
        newDate.setHours(date[0]);
        newDate.setMinutes(date[1]);
        return newDate;
    } else {
        date = new Date(date);
        return ('0' + date.getHours())?.slice(-2) + ':' + ('0' + date.getMinutes())?.slice(-2);
    }
};

export const convertUTCTimeToUserTimeZone = (hours: number, minutes: number, userTimeZoneOffset = +530) => {
    // Create a Date object for the UTC time
    const utcDate = new Date();
    utcDate.setUTCHours(hours);
    utcDate.setUTCMinutes(minutes);

    // Calculate the UTC timestamp
    const utcTimestamp = utcDate.getTime();

    // Adjust for the user's time zone offset
    const userTimestamp = utcTimestamp + userTimeZoneOffset * 60 * 1000;

    // Create a Date object for the user's local time
    const userDate = new Date(userTimestamp);

    // Get hours and minutes in 24-hour format
    const userHours = userDate.getHours();
    const userMinutes = userDate.getMinutes();

    // Format minutes with leading zero if needed
    const formattedMinutes = (userMinutes < 10 ? '0' : '') + userMinutes;

    // Return the formatted user's local time
    return `${userHours}:${formattedMinutes}`;
};

export const convertToUTC = (userTimeString: string) => {
    // Parse the input time string
    const [hours, minutes] = userTimeString.split(':');
    const parsedHours = parseInt(hours, 10);
    const parsedMinutes = parseInt(minutes, 10);

    // Create a Date object with current date
    const currentDate = new Date();

    // Set hours and minutes
    currentDate.setHours(parsedHours);
    currentDate.setMinutes(parsedMinutes);

    // Get the user's time zone offset in minutes
    const userTimeZoneOffset = currentDate.getTimezoneOffset();

    // Determine if daylight saving time is in effect
    const isDaylightSavingTime = (() => {
        const januaryOffset = new Date(currentDate.getFullYear(), 0, 1).getTimezoneOffset();
        const julyOffset = new Date(currentDate.getFullYear(), 6, 1).getTimezoneOffset();
        return Math.max(januaryOffset, julyOffset) !== userTimeZoneOffset;
    })();

    // Adjust the offset if daylight saving time is in effect
    const effectiveTimeZoneOffset = userTimeZoneOffset + (isDaylightSavingTime ? 60 : 0);

    // Calculate the local time by adding the effective time zone offset
    const localTime = currentDate.getTime() + effectiveTimeZoneOffset * 60 * 1000;

    // Create a Date object for the local time
    const localDate = new Date(localTime);

    // Convert local time to UTC time
    const utcTime = new Date(localDate.getTime() - effectiveTimeZoneOffset * 60 * 1000);

    // Get hours and minutes in 24-hour format
    const utcHours = utcTime.getUTCHours();
    const utcMinutes = utcTime.getUTCMinutes();

    // Format minutes with leading zero if needed
    const formattedMinutes = (utcMinutes < 10 ? '0' : '') + utcMinutes;

    // Return the formatted UTC time
    return `${utcHours < 10 ? `0${utcHours}` : utcHours}:${formattedMinutes}`;
};

export const convertUTCTimeToIST = (hours: number, minutes: number) => {
    // Convert hours and minutes to milliseconds
    const utcMilliseconds = hours * 60 * 60 * 1000 + minutes * 60 * 1000;

    // IST offset in milliseconds (5 hours 30 minutes)
    const istOffset = 5.5 * 60 * 60 * 1000;

    // Add IST offset to UTC milliseconds
    const istMilliseconds = utcMilliseconds + istOffset;

    // Convert milliseconds to IST time
    const istHours = Math.floor(istMilliseconds / (60 * 60 * 1000));
    const istMinutes = Math.floor((istMilliseconds % (60 * 60 * 1000)) / (60 * 1000));

    // Format IST time
    const formattedISTTime = (istHours < 10 ? '0' : '') + istHours + ':' + (istMinutes < 10 ? '0' : '') + istMinutes;

    return formattedISTTime;
};

const getPrivileges = () => {
    const userString = AuthUser();
    const user: any = userString ? userString : {};
    return user?.privileges || [];
};

export const IsPrivAuthorized = (privRequired: string): boolean => {
    const privs: string[] | undefined = getPrivileges();
    // To Check Specific Previleges condition

    if (privs?.includes(privRequired)) {
        return true;
    } else return false;
};

export const convertToTitleCase = (string: any) => {
    string = string?.toLowerCase().replace(/\b\w/g, (letter: any) => {
        return letter?.toUpperCase();
    });
    return string;
};

export const generateValidationSchema = (
    formData: any[],
    yup: any,
    validationPattern?: any,
    onDynamicFieldDetected?: (field: any) => void
) => {
    const schema: any = {};
    formData.forEach(
        (field: {
            type?: any;
            label: any;
            initialValue?: any;
            mandatory: any;
            minLen: any;
            maxLen: any;
            notAllowZeros?: any;
            pinCode?: any;
            phoneNumber?: any;
            value?: any;
            regex?: any;
            parent?: any;
            dynamic?: any;
        }) => {
            let validation: any;
            // Initialize validation based on field type
            switch (field?.type) {
                case 'text':
                    validation = yup.string();
                    break;
                case 'number':
                    validation = yup.number();
                    break;
                case 'object':
                    validation = field.mandatory ? yup.object() : yup.object().nullable();
                    break;
                case 'decimal':
                    validation = field.mandatory
                        ? yup.string().required('Decimal value is required').matches(/^-?\d*(\.\d+)?$/, 'Must be a valid decimal number')
                        : yup.string().matches(/^-?\d*(\.\d+)?$/, 'Must be a valid decimal number');
                    break;

                case 'email':
                    validation = yup.string().email(`${field.label} must be a valid email address`);
                    break;

                case 'tel':
                    if (!validationPattern && field?.initialValue?.validationPattern) {
                        validation = yup
                            .string()
                            .matches(new RegExp(field?.initialValue?.validationPattern), `${field.label} is not valid`);
                    } else if (validationPattern) {
                        validation = yup.string().matches(new RegExp(validationPattern), `${field.label} is not valid`);
                        // .max(validationPattern.length, `${field.label} cannot exceed ${validationPattern.length} characters`);
                    } else {
                        validation = yup.string();
                    }
                    break;

                case 'array':
                    validation = field.mandatory ? yup.array().min(1, `Please select at least one ${field.label}`) : yup.array();
                    break;
                // case 'zoom':
                //     console.log('formdata',formData)
                //     break
                case 'emptyDate':
                    break;
                default:
                    validation = yup.string();
            }
            const { label, mandatory, minLen, maxLen, regex, dynamic } = field;
            if (mandatory) {
                validation = validation.required(`${label} is required`);
            }
            if (minLen) {
                validation = validation.min(field.minLen, `${label} must be at least ${field.minLen} characters`);
            }
            if (maxLen && field.type === 'number') {
                validation = validation.test(
                    'max-length',
                    `${label} should be less than ${maxLen} digits`,
                    (value: { toString: () => { (): any; new(): any; length: any } }) => !value || value?.toString().length <= maxLen
                );
            } else if (maxLen) {
                validation = validation.max(field.maxLen, `${label} cannot exceed ${field.maxLen} characters`);
            }
            if (dynamic) {
                onDynamicFieldDetected?.(field);
            }

            if (field.notAllowZeros) {
                if (field.pinCode) {
                    validation = validation.test(
                        'not-all-zeros',
                        'Invalid PIN code. All zeros not allowed.',
                        (value: string) => value !== '000000'
                    );
                } else if (field.phoneNumber) {
                    validation = validation.test(
                        'not-all-zeros',
                        'Invalid Phone Number. All zeros not allowed.',
                        (value: string) => value !== '0000000000'
                    );
                } else {
                    validation = validation.test(
                        'not-zero-value',
                        `${label} must be greater than 0`,
                        (value: string) => !value || parseFloat(value) > 0
                    );
                }
            }

            if (regex) {
                if (field.type === 'text') {
                    validation = validation?.matches(new RegExp(regex), `${label} must be ${handleRegexTypeMsg(field)}`);
                } else {
                    validation = validation?.matches(new RegExp(regex), `${label} is not valid`);
                }
            }
            schema[field.value] = validation;
        }
    );

    return yup.object().shape(schema);
};

export const generateInitialValues = (formData: any[]) => {
    const initialValues: any = {};
    formData?.forEach((field) => {
        if (field?.inputgroupKey && field?.initialValue?.for) {
            initialValues[field?.initialValue?.for] = field?.initialValue || '';
            initialValues[field.value] = '';
        } else {
            initialValues[field?.value] = field?.initialValue || '';
        }
    });
    return initialValues;
};

export const handleValidationSchema = (formSchema?: any[]) => {
    const validationSchema: any = [];
    formSchema?.forEach(
        (obj: { fieldName: any; label: any; pattern: any; required: any; maxlength: any; minlength: any; inputType: { value?: any } }) => {
            if (obj?.fieldName) {
                const schema: any = {};
                const validations: any = {};
                schema['name'] = obj.fieldName;
                schema['label'] = obj.label;
                schema['pattern'] = obj.pattern ? obj.pattern : '';
                validations['nodeValue'] = [];
                if (obj.required) {
                    validations['nodeValue'].push('required');
                }
                if (obj.maxlength) {
                    validations['nodeValue'].push('maxLength');
                    validations['maxlength'] = obj.maxlength;
                }
                if (obj.minlength) {
                    validations['nodeValue'].push('minLength');
                    validations['minlength'] = obj.minlength;
                }
                if (obj.inputType && Object.keys(obj.inputType).length > 0) {
                    validations['nodeValue'].push(obj?.inputType?.value);
                }
                schema['value'] = '';
                schema['validations'] = validations;
                validationSchema.push(schema);
            }
        }
    );
    return validationSchema;
};

export const restrictSpace = (e: any, props: any, validation: any = {}) => {
    if (props.alphanumericspecial) {
        const pattern = new RegExp(/^[ A-Za-z0-9_@.,~{}<>?`":;|()[!$=%^*()/#&+-]*$/g);

        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.alphanumeric) {
        const pattern = new RegExp(/^[A-Za-z0-9 ]+$/g);

        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.alphabets) {
        const pattern = new RegExp(/^[a-zA-Z\s]*$/);
        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.percentage) {
        const pattern = new RegExp(/^([0-9%])$/);
        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.alphabetsandspecialchars) {
        const pattern = new RegExp(/^[ a-zA-Z\n_@,~{}!=$<>?":|`%[;^*()./#&+-/s]*$/g);
        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.alphabetsandspecs) {
        const pattern = new RegExp(/^[a-zA-Z-.()\s]*$/g);

        if (!pattern.test(e.key)) {
            e.preventDefault();
        }
    }

    if (props.floatdecimal) {
        const regex = /^0(?! \d+$)/;

        const stringRegex = /^[a-zA-Z!@#$%^&*)(+=,_-]$/;

        if (stringRegex.test(e.key) || e.which === 32 || (e?.currentTarget?.selectionStart === 0 && e.which === 190)) e.preventDefault();

        if (regex.test(e.key) && e?.currentTarget?.selectionStart === 0) {
            e.preventDefault();
        }
    }

    if (props.maxval) {
        const val = Number(validation.values[props.value] + e.key);
        if (val > props.maxval) {
            e.preventDefault();
        }
    }

    if (props.minval) {
        const val = Number(validation.values[props.value] + e.key);

        if (val < props.minval) {
            e.preventDefault();
        }
    }

    if (props.email) {
        const reg = new RegExp('^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$');

        const stringRegex = /^[!#$%^&*)([+=,:;<>/{}`|~'?"]$/;

        if (stringRegex.test(e.key) || e.which === 32 || (e?.currentTarget?.selectionStart === 0 && e.which === 190)) {
            e.preventDefault();
        }

        if (!reg.test(e.key) && e?.currentTarget?.selectionStart === 0) {
            e.preventDefault();
        }
    }

    if (props.numbers) {
        const reg = new RegExp('^[0-9]+$');

        const stringRegex = /^[a-zA-Z!@#$%^&*)([+=,:;<>/{}`|~'?"_-]$/;

        if (stringRegex.test(e.key) || e.which === 32 || (e?.currentTarget?.selectionStart === 0 && e.which === 190)) {
            e.preventDefault();
        }

        if (!reg.test(e.key) && e?.currentTarget?.selectionStart === 0) {
            e.preventDefault();
        }
    }

    if (e.which === 32 && e?.currentTarget?.selectionStart === 0) e.preventDefault();

    if (props.type === 'number' && (e.which === 69 || e.which === 189 || e.which === 187 || e.which === 190)) e.preventDefault();
};

const handleBookingStatus = (status: string, obj: any) => {
    switch (status.toUpperCase()) {
        case 'ASSIGNED':
            obj.hideTakeAction = true;
            obj.hideSwitch = false;
            obj.showEdit = obj?.isActive;
            obj.showReassign = false;
            obj.showComment = !obj?.isActive;
            return { text: 'Assigned', class: 'statusOrange' };
        case 'OPEN':
            obj.hideSwitch = false;
            obj.showEdit = false;
            obj.showReassign = obj.pilotStatus === '' && obj?.PilotComment !== '';
            // obj.showComment = !obj?.isActive;
            if (obj.comment) {
                obj.hideTakeAction = true;
            } else if (obj?.PilotComment) {
                obj.hideTakeAction = obj.pilotStatus === '' && obj?.PilotComment !== '';
            } else {
                obj.hideTakeAction = !obj?.isActive;
            }

            if (obj.comment) {
                obj.showComment = true;
            } else {
                obj.showComment = !obj?.isActive;
            }
            return { text: 'Open', class: 'statusBlue' };
        case 'ASSIGNED_TO_PILOT':
        case 'ASSIGNED TO PILOT':
            obj.hideTakeAction = true;
            obj.hideSwitch = false;
            obj.showEdit = true;
            obj.showComment = obj?.isActive ? false : true;
            return { text: 'Assigned to Pilot', class: 'statusPurple' };
        case 'ACCEPTED_BY_PILOT':
        case 'ACCEPTED BY PILOT':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showEdit = false;
            return { text: 'Accepted by pilot', class: 'statusPurple' };
        case 'PARTIALLY_PAID':
        case 'PARTIALLY PAID':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Partially Paid', class: 'statusAmber' };
        case 'NOT_YET_PAID':
        case 'NOT YET PAID':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Not yet paid', class: 'statusRed' };
        case 'REJECTED':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Rejected', class: 'statusRed' };
        case 'APPROVED':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Approved', class: 'statusGreen' };
        case 'IN_PROGRESS':
        case 'IN PROGRESS':
            // case 'In progress':
            obj.hideTakeAction = true;
            obj.hideSwitch = false;
            obj.showEdit = false;
            obj.showComment = obj?.isActive ? false : true;
            return { text: 'In Progress', class: 'statusYellow' };
        case 'PENDING':
            // case 'In progress':
            obj.hideTakeAction = true;
            obj.hideSwitch = false;
            obj.showEdit = false;
            obj.showComment = obj?.isActive ? false : true;
            return { text: 'Pending', class: 'statusYellow' };
        case 'PARTIALLY_APPROVED':
        case 'PARTIALLY APPROVED':
            // case 'In progress':
            obj.hideTakeAction = true;
            obj.hideSwitch = false;
            obj.showEdit = false;
            obj.showComment = obj?.isActive ? false : true;
            return { text: 'Partially Pending', class: 'statusYellow' };
        case 'COMPLETED':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Completed', class: 'statusGreen' };
        case 'CANCELLED':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showEdit = false;
            return { text: 'Cancelled', class: 'statusGrey' };
        case 'PAYMENT_PENDING':
            obj.hideTakeAction = true;
            obj.hideSwitch = true;
            obj.showTrackButton = true;
            obj.showEdit = false;
            return { text: 'Payment Pending', class: 'statusRed' };
        default:
            // obj.hideTakeAction = true;
            return { text: convertToTitleCase(status), class: 'primary' };
    }
};

export const handleSwitchModalBody = (status: { isActive: boolean }) => {
    return (
        <>
            <div className="fs-14">Are you sure you want to {status?.isActive === false ? 'activate' : 'inactivate'} the record?</div>
        </>
    );
};

export const handleUnassignSwitchModalBody = (status: { isActive: boolean }) => {
    return (
        <>
            <div className="fs-14">Are you sure you want to unassign the record?</div>
        </>
    );
};

export const combineDateAndTime = (date: string, time: string) => {
    const data = {
        sprayDate: date,
        sprayTime: convertToUTC(time)
    };
    // Split the sprayTime into hours and minutes
    const [hours, minutes] = data.sprayTime.split(':');
    // Pad the month and day with leading zeros if necessary
    const [year, month, day] = data.sprayDate.split('-').map((val) => val.padStart(2, '0'));
    // Combine date and time and format it
    const combinedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:00.000+00:00`;
    return combinedDateTime;
};

// const mapProfileDetailsToSchema = (schema: any, details: any) => {
//     return schema?.map((item: any) => {
//         if (item?.customCell && item?.fieldType === 'dateFormat') {
//             return {
//                 ...item,
//                 value: getDate(details[item?.accessor], false)
//             };
//         }
//         return {
//             ...item,
//             value: details[item.accessor] || '-'
//         };
//     });
// };

const mapProfileDetailsToSchema = (schema: any, details: any) => {
    return schema?.reduce((acc: any[], item: any) => {
        const key = item.accessor;

        // Safe check using Object.prototype.hasOwnProperty.call
        if (Object.prototype.hasOwnProperty.call(details, key) && details[key] != null) {
            const value = item?.customCell && item?.fieldType === 'dateFormat' ? getDate(details[key], false) : details[key] || '-';

            acc.push({ ...item, value });
        }

        return acc;
    }, []);
};

// const getLastSevenDaysDateRange = () => {
//     const formatDate = (date: any) => {
//         const day = String(date.getDate()).padStart(2, '0');
//         const month = String(date.getMonth() + 1).padStart(2, '0'); // Add 1 to month since it is zero-based
//         const year = date.getFullYear();
//         return `${day}/${month}/${year}`;
//     };

//     const currentDate = new Date();
//     const endDate = formatDate(currentDate);

//     const startDateObject = new Date();
//     startDateObject.setDate(currentDate.getDate() - 7);
//     const startDate = formatDate(startDateObject);

//     return `${startDate} to ${endDate}`;
// };

export const userSingleNameLabel = (userName: string) => {
    const name = userName?.split(' ');
    if (name?.length === 1) return name?.[0]?.charAt(0)?.toUpperCase();
    else return name?.[0]?.charAt(0)?.toUpperCase() + name?.[1]?.charAt(0)?.toUpperCase();
};

export function checkIsActiveFalse(obj: any) {
    return obj?.isActive !== false;
}

export const handleTableActions = (obj: any) => {
    obj.hideTakeAction = true;
    obj.hideSwitch = false;
    obj.showEdit = obj?.isActive;
    obj.showComment = !obj?.isActive;
    // obj.hideSetting = true;
    obj.hideSetting = obj?.isActive;
    obj.showUnpair = true;

    return obj;
};

export const defaultlastNdays = (days?: any) => {
    // Binding Last 30 Days to the Dashboard and Showing last 30 days data
    const today: any = new Date();
    const lastNDays: any = new Date(today.getTime() - (days ? days : 30) * 24 * 60 * 60 * 1000);

    return {
        lastNDays,
        today
    };
};

// For filtering '',undefined,null,[],{}
export const filterValidParams = (body: any, shouldFilterQuery = false) => {
    const filterObject = (obj: any) =>
        Object.fromEntries(
            Object.entries(obj || {}).filter(
                ([_, value]) =>
                    value !== null &&
                    value !== undefined &&
                    value !== '' &&
                    !(Array.isArray(value) && value.length === 0) &&
                    !(typeof value === 'object' && Object.keys(value).length === 0)
            )
        );

    const result = filterObject(body);

    if (shouldFilterQuery && result.query && typeof result.query === 'object') {
        const filteredQuery = filterObject(result.query);
        if (Object.keys(filteredQuery).length > 0) {
            result.query = filteredQuery;
        } else {
            delete result.query; // Remove empty query key
        }
    }

    return result;
};

const droneServiceActions = (row: any, column: any, index: number, props: any) => {
    return (
        <div className="d-flex align-items-center">
            {(row.original.view || column.view) && (
                <IsAuthorized
                    privRequired={props.view}
                    yes={() => (
                        <>
                            <img
                                id={`view-${index}`}
                                src={view}
                                alt="view"
                                className="me-2"
                                onClick={() => props.handleServiceView(row, column, index)}
                            />
                            {Tooltip(`view-${index}`, 'View')}
                        </>
                    )}
                />
            )}
            {(row.original.mines || column.mines) && (
                <IsAuthorized
                    privRequired={props?.priv?.mines}
                    yes={() => (
                        <>
                            <img
                                id={`mine-${index}`}
                                src={mines}
                                alt="mines"
                                className="me-2"
                                onClick={() => props.handleServiceMine(row, column, index)}
                            />
                            {Tooltip(`mine-${index}`, 'Mines')}
                        </>
                    )}
                />
            )}
            {(row.original.deliverables || column.deliverables) && (
                <IsAuthorized
                    privRequired={props?.priv?.deliverables}
                    yes={() => (
                        <>
                            <img
                                id={`deliverables-${index}`}
                                src={addMine}
                                alt="deliverables"
                                className="me-2"
                                onClick={() => props.handleServiceAddMine(row, column, index)}
                            />
                            {Tooltip(`deliverables-${index}`, 'deliverables')}
                        </>
                    )}
                />
            )}

            {/* view add spareParts icon */}
            {(row.original.viewRequestedParts || column.viewRequestedParts) && (
                <IsAuthorized
                    privRequired={props.viewRequestPartsCondition}
                    yes={() => (
                        <>
                            <i
                                // title="View Requested Parts"
                                id={`viewRequestedParts-${index}`}
                                className={`ri-file-list-3-fill me-2 fs-20 ${row.original.requestedPartsDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-success'
                                    }`}
                                aria-disabled={row.original.requestedPartsDisabled}
                                onClick={
                                    row.original.requestedPartsDisabled
                                        ? undefined
                                        : () => props.handleViewRequestedParts(row, column, index)
                                }
                            />
                            {Tooltip(`viewRequestedParts-${index}`, 'View Requested Parts')}
                        </>
                    )}
                />
            )}

            {(row.original.assign || column.assign) && (
                <IsAuthorized
                    privRequired={props.assignCondition}
                    yes={() => (
                        <div>
                            <Button
                                color="bg-primary-light text-primary"
                                className={`border-primary fs-10 me-1 ${row.original.assignDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                id={`assign-${index}`}
                                onClick={row.original.assignDisabled ? undefined : () => props.onClickAssign(row, column, index)}
                                disabled={row.original.assignDisabled}
                            >
                                Assign
                            </Button>
                            {Tooltip(`assign-${index}`, 'Assign')}
                        </div>
                    )}
                />
            )}

            {(row.original.reAssign || column.reAssign) && (
                <IsAuthorized
                    privRequired={props.reAssignCondition}
                    yes={() => (
                        <div className="form-reAssign form-reAssign-sm">
                            <Button
                                color="bg-primary-light text-primary"
                                className={`border-primary fs-10 me-1 ${row.original.reAssignDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                id={`reAssign-${index}`}
                                onClick={row.original.reAssignDisabled ? undefined : () => props.onClickReAssign(row, column, index)}
                                disabled={row.original.reAssignDisabled}
                            >
                                ReAssign
                            </Button>
                            {Tooltip(`reAssign-${index}`, 'ReAssign')}
                        </div>
                    )}
                />
            )}

            {(row.original.edit || column.edit) && (
                <IsAuthorized
                    privRequired={props?.priv?.edit}
                    yes={() => (
                        <>
                            {/* <i
                                title="Edit"
                                id={`edit-${index}`}
                                className={`ri-edit-box-line me-2 fs-20 ${
                                    row.original.editDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-success'
                                }`}
                                aria-disabled={row.original.editDisabled}
                                onClick={row.original.editDisabled ? undefined : () => props.handleEdit(row, column, index)}
                            /> */}
                            <img
                                src={edit}
                                id={`edit-${index}`}
                                alt="edit"
                                title="Edit"
                                // className="me-2"
                                className={`me-2 ${props.disabledActionbtn ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-danger'
                                    }`}
                                onClick={row.original.editDisabled ? undefined : () => props.handleEdit(row, column, index)}
                            />
                            {Tooltip(`edit-${index}`, 'Edit')}
                        </>
                    )}
                />
            )}

            {(row.original.download || column.download) && (
                <IsAuthorized
                    privRequired={props.downloadFileCondition}
                    yes={() => (
                        <>
                            <i
                                id={`downloadFile-${index}`}
                                onClick={row.original.downloadDisabled ? undefined : () => props.handleDownloadFile(row, column, index)}
                                className={`ri-download-2-line me-2 fs-18 ${row.original.downloadDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-primary'
                                    }`}
                                aria-disabled={row.original.downloadDisabled}
                            />
                            {Tooltip(`downloadFile-${index}`, 'Download')}
                        </>
                    )}
                />
            )}

            {(row.original.delete || column.delete) && (
                <IsAuthorized
                    privRequired={props?.priv?.delete}
                    yes={() => (
                        <>
                            {/* <i
                                title="Delete"
                                id={`delete-${index}`}
                                className={`ri-delete-bin-line fs-18 ${
                                    row.original.deleteDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-danger'
                                }`}
                                aria-disabled={row.original.deleteDisabled}
                                onClick={row.original.deleteDisabled ? undefined : () => props.handleDelete(row, column, index)}
                            /> */}
                            <img
                                src={deleteImage}
                                id={`delete-${index}`}
                                alt="delete"
                                // className="me-2"
                                className={`me-2 ${props.disabledActionbtn ? 'text-muted cursor-not-allowed' : 'cursor-pointer link-danger'
                                    }`}
                                onClick={row.original.deleteDisabled ? undefined : () => props.handleDelete(row, column, index)}
                            />
                            {Tooltip(`delete-${index}`, 'Delete')}
                        </>
                    )}
                />
            )}
            {(row.original.checkbox || column.checkbox) && (
                <div className="d-flex align-items-center justify-content-center">
                    <input
                        type="checkbox"
                        checked={row.original.serviceChecked}
                        disabled={row.original.disableServiceCheckbox === true ? true : false}
                        className={
                            'form-check-input ' + (row.original.disableServiceCheckbox ? 'border border-danger' : 'border border-primary')
                        }
                        onChange={(e: any) => props?.handleServiceCheckboxChange(e, row.index, row, column)}
                    />
                </div>
            )}
            {(row.original.takeAction || column.takeAction) && (
                <IsAuthorized
                    privRequired={props.takeAction}
                    yes={() => (
                        <div className="form-reAssign form-reAssign-sm">
                            <Button
                                size="sm"
                                type="button"
                                color="success"
                                className={`border-primary fs-10 me-1 ${row?.original?.reAssignDisabled ? 'text-muted cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                id={`takeAction-${index}`}
                                onClick={row?.original?.reAssignDisabled ? undefined : () => props?.handleTakeAction(row, column, index)}
                                disabled={row?.original?.reAssignDisabled}
                            >
                                Take Action
                            </Button>
                            {Tooltip(`takeAction-${index}`, 'Take Action')}
                        </div>
                    )}
                />
            )}
            {(row.original.viewDetails || column.viewDetails) && (
                <IsAuthorized
                    privRequired={props.viewDetails}
                    yes={() => (
                        <div className="form-reAssign form-reAssign-sm">
                            <Button
                                size="sm"
                                type="button"
                                color=""
                                className={'grey-button'}
                                id={`viewDetails-${index}`}
                                onClick={row.original.reAssignDisabled ? undefined : () => props.handleServiceView(row, column, index)}
                                disabled={row.original.reAssignDisabled}
                            >
                                View Details
                            </Button>
                            {Tooltip(`viewDetails-${index}`, 'View Details')}
                        </div>
                    )}
                />
            )}
            {(row?.original?.unAssign || column?.unAssign) && (
                <IsAuthorized
                    privRequired={props?.unAssignCondition}
                    yes={() => (
                        <>
                            <span>
                                <div className="form-unassign form-unassign-sm">
                                    <Button
                                        outline
                                        color="danger"
                                        className={`border-danger fs-6 me-2 ${row?.original?.unAssignDisabled || column?.unAssignDisabled
                                            ? 'text-muted cursor-not-allowed'
                                            : 'cursor-pointer link-danger'
                                            }`}
                                        id={`unAssign-${index}`}
                                        onClick={() => {
                                            if (!(row?.original?.unAssignDisabled || column?.unAssignDisabled)) {
                                                props.onClickUnassign(row, column, index);
                                            }
                                        }}
                                        aria-disabled={row?.original?.unAssignDisabled || column?.unAssignDisabled || false}
                                        disabled={row?.original?.unAssignDisabled || column?.unAssignDisabled || false}
                                    >
                                        Unassign
                                    </Button>
                                </div>
                            </span>
                        </>
                    )}
                />
            )}

            {row?.original?.switch ||
                (column?.switch && (
                    <span>
                        <div className="form-switch form-switch-sm">
                            <Input
                                className={`form-check-input cursor-pointer ${column.className ? column.className : ''}`}
                                type="checkbox"
                                role="switch"
                                name={'status'}
                                id={`switch-${index}`}
                                disabled={row.original.disabled}
                                onChange={(e: any) => props.onClickSwitch(row, column, index)}
                                checked={row.original.isActive}
                            />
                            {/* {Tooltip(`switch-${index}`, row.original.isActive ? 'Active' : 'Inactive')} */}
                        </div>
                    </span>
                ))}
            {
                // row?.original?.comment ||
                column?.comment && (
                    <IsAuthorized
                        privRequired={props.editor}
                        yes={() => (
                            <>
                                {!row?.original?.isActive && (
                                    <span>
                                        <i className="ri-message-2-line cursor-pointer text-primary fs-18" id={`pop-${index}`}></i>
                                        <UncontrolledPopover placement="bottom" target={`pop-${index}`}>
                                            <PopoverHeader className="fs-8">Inactive Comment</PopoverHeader>
                                            <PopoverBody>{row?.original?.comment}</PopoverBody>
                                        </UncontrolledPopover>
                                    </span>
                                )}
                            </>
                        )}
                    />
                )
            }
            {(row.original.viewComment || column?.viewComment) && (
                <IsAuthorized
                    privRequired={props.viewComment}
                    yes={() => (
                        <>
                            <img
                                src={Comment}
                                alt=""
                                className="me-2 fs-18 cursor-pointer link-success"
                                id={`viewComment-${index}`}
                                onClick={() => props.handleServiceViewComment(row, column, index)}
                            />
                            {Tooltip(`viewComment-${index}`, 'View Comment')}
                        </>
                    )}
                />
            )}
        </div>
    );
};

export const getLoginUserMenuRoutes = (privileges: any, navData: any) => {
    const menuItemsList: any = [];

    navData?.forEach((item: any) => {
        if (item?.subItems?.length) {
            const subMenu = item?.subItems?.filter((subItem: any) => {
                const roles = [subItem?.create, subItem?.view, subItem?.edit, subItem?.delete].filter(Boolean);
                // const roles = [subItem?.view].filter(Boolean);

                return roles?.some((role) => privileges?.includes(role?.toUpperCase()));
            });

            if (subMenu?.length) {
                menuItemsList?.push({ ...item, subItems: subMenu });
            }
        } else {
            const roles = [item?.create, item?.view, item?.edit, item?.delete].filter(Boolean);
            // const roles = [item?.view].filter(Boolean);

            const hasPrivilege = roles?.some((role) => privileges?.includes(role?.toUpperCase()));
            if (hasPrivilege) {
                menuItemsList?.push(item);
            }
        }
    });

    return menuItemsList;
};

const capitalize = (str: string) => {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const hasPrivilege = (privilege: string) => {
    const userDetails = AuthUser();
    let privileges = userDetails?.userProfile?.roles?.[0]?.privileges;
    if (userDetails?.userProfile?.roles?.length > 1) {
        privileges = store.getState()?.SelectedRole?.role?.privileges;
    }
    return privileges?.includes(privilege);
};

export function DownloadFileUrl(link: string, name: string) {
    const a = document.createElement('a');
    a.href = link;
    a.download = name;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

export async function DownloadFileUrlBlob(link: string, name: string) {
    const response = await fetch(link);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
}

const encryptDynamicPath = (url: string): string => {
    const [baseUrl, queryString] = url.split('?');

    const matchingPattern = routePatterns.find((pattern) => {
        const matcher = match(pattern, { decode: decodeURIComponent });
        return matcher(baseUrl) !== false;
    });

    if (!matchingPattern) {
        return url; // No match, skip encryption
    }

    // Ex:
    // const matcher = match('/areas/action/:areaId');
    // const matched = matcher('/areas/action/123');

    const matcher = match(matchingPattern, { decode: decodeURIComponent });
    const matched = matcher(baseUrl);

    if (!matched) return url;

    const encryptedParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(matched.params)) {
        encryptedParams[key] = encrypt(value as string); // Encrypt any param value, not just numeric
    }

    // Replace original params with encrypted ones
    let encryptedPath = matchingPattern;
    for (const [key, encryptedVal] of Object.entries(encryptedParams)) {
        encryptedPath = encryptedPath.replace(`:${key}`, encryptedVal);
    }

    return encryptedPath; // ✅ Just the encrypted path, no query string
};

const vectorData = (data: any) => {
    let PreparedData = data?.map((layer: any) => {
        let obj = {
            type: 'Feature',
            geometry: layer?.Coordinates,
            properties: {
                name: layer?.FeatureName,
                additionalProperty: 'Value B',
                isBufferedLayer: layer?.bufferDistance != null ? true : false,
                bufferDistance: layer?.bufferDistance != null ? layer?.bufferDistance : ''
            }
        };

        const vectorSource = new VectorSource({
            features: new GeoJSON()?.readFeatures(obj)
        });
        let style;
        if (layer?.Coordinates?.type === 'Point') {
            style = new Style({
                image: new Circle({
                    radius: 3,
                    fill: new Fill({
                        color: layer?.bufferDistance != null ? 'rgba(255, 0, 0, 0.3)' : layer?.Color
                    }),
                    stroke: new Stroke({
                        color: layer?.bufferDistance != null ? 'red' : layer?.Color,
                        width: 2
                    })
                })
            });
        } else if (layer?.Coordinates?.type === 'Polygon') {
            style = new Style({
                fill: new Fill({
                    color: layer?.bufferDistance != null ? 'rgba(255, 0, 0, 0.3)' : layer?.Color
                }),
                stroke: new Stroke({
                    color: layer?.bufferDistance != null ? 'red' : layer?.Color,
                    width: 2
                })
            });
        } else if (layer?.Coordinates?.type === 'LineString') {
            style = new Style({
                fill: new Fill({
                    color: layer?.bufferDistance != null ? 'rgba(255, 0, 0, 0.3)' : layer?.Color
                }),
                stroke: new Stroke({
                    color: layer?.bufferDistance != null ? 'rgba(255, 0, 0, 0.3)' : layer?.Color, // Set your desired stroke color here
                    width: layer?.bufferDistance != null ? 20 : 2 // Set the stroke width
                })
            });
        }
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: style,
            properties: {
                isBufferedLayer: layer?.bufferDistance != null ? true : false
            }
        });
        return vectorLayer;
    });

    return PreparedData;
};

export const displayFirstLastName: any = () => {
    const userDetails = AuthUser();
    let name = userDetails?.firstName?.slice(0, 1) + userDetails?.lastName?.slice(0, 1);
    if (userDetails?.userProfile?.roles?.[0]?.role === 'GUEST') {
        name = 'GUEST';
    }
    return name;
};

export const getAllCountries = () => Country.getAllCountries().map((obj: any) => ({ label: obj.name, value: obj.isoCode }));

export const getAllStates = () => State.getAllStates().map((obj: any) => ({ label: obj.name, value: obj.isoCode }));

export const getAllCities = () => City.getAllCities().map((obj: any) => ({ label: obj.name, value: obj.isoCode }));

export const getStatesOfCountry = (countryCode: any) =>
    State.getStatesOfCountry(countryCode).map((obj: any) => ({ ...obj, label: obj.name, value: obj.isoCode }));

export const getCitiesOfState = (countryCode: any, stateCode: any) =>
    City.getCitiesOfState(countryCode, stateCode).map((obj: any) => ({ label: obj.name, value: obj.name }));


export const formatLengthInMeters = function (line: any) {
    const length = getLength(line);
    let output;
    output = `${Math.round(length * 100) / 100} m`;
    return output;
};


// Your exact hexToRgba function
export const hexToRgba = (hex: string, alpha: number): string => {
    const bigint = parseInt(hex.replace('#', ''), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Convert hex to KML color format (AABBGGRR)
export const hexToKMLColor = (hex: string): string => {
    hex = hex.replace('#', '');

    if (hex.length === 6) {
        const bb = hex.substring(4, 6);
        const gg = hex.substring(2, 4);
        const rr = hex.substring(0, 2);
        return `ff${bb}${gg}${rr}`; // Fully opaque
    }

    return 'ff0000ff'; // Default to opaque red
};

// Convert rgba to KML color format (AABBGGRR)
export const rgbaToKMLColor = (rgba: string): string => {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match) return 'ff0000ff';

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);
    const a = match[4] ? Math.round(parseFloat(match[4]) * 255) : 255;

    // Convert to hex and KML format (AABBGGRR)
    const alphaHex = a.toString(16).padStart(2, '0');
    const blueHex = b.toString(16).padStart(2, '0');
    const greenHex = g.toString(16).padStart(2, '0');
    const redHex = r.toString(16).padStart(2, '0');

    return `${alphaHex}${blueHex}${greenHex}${redHex}`;
};


// --------------------------------------------
// REUSABLE FILE GENERATORS
// --------------------------------------------

// PRJ File content for WGS84
// export const createPrjFileContent = () => `
// GEOGCS["WGS 84",
//     DATUM["WGS_1984",
//         SPHEROID["WGS 84",6378137,298.257223563,
//             AUTHORITY["EPSG","7030"]],
//         AUTHORITY["EPSG","6326"]],
//     PRIMEM["Greenwich",0,
//         AUTHORITY["EPSG","8901"]],
//     UNIT["degree",0.0174532925199433,
//         AUTHORITY["EPSG","9122"]],
//     AUTHORITY["EPSG","4326"]]
// `;

// // Convert OL features to GeoJSON safely
// export const convertFeaturesToGeoJSON = (features: any[]) => {
//     const converted: any = features
//         .map((feature: any) => {
//             const geom = feature.getGeometry();
//             if (!geom) return null;

//             let newGeom: any;
//             const type = geom.getType();

//             if (type === "Polygon") {
//                 newGeom = new Polygon(
//                     geom.getCoordinates().map((ring: any[]) =>
//                         ring.map((c) => transform(c, "EPSG:3857", "EPSG:4326"))
//                     )
//                 );
//             } else if (type === "LineString") {
//                 newGeom = new LineString(
//                     geom.getCoordinates().map((c: any) =>
//                         transform(c, "EPSG:3857", "EPSG:4326")
//                     )
//                 );
//             } else if (type === "Point") {
//                 newGeom = new Point(
//                     transform(geom.getCoordinates(), "EPSG:3857", "EPSG:4326")
//                 );
//             } else return null;

//             const f = new Feature(newGeom);

//             // copy properties
//             const props = feature.getProperties();
//             Object.keys(props).forEach((key) => {
//                 if (key !== "geometry") f.set(key, props[key]);
//             });

//             return f;
//         })
//         .filter(Boolean);

//     const format = new GeoJSON();
//     return JSON.parse(
//         format.writeFeatures(converted)
//     );
// };

// // Create SHP ZIP
// export const generateShapefileZip = async (geojson: any, fileName: string) => {
//     const zip = new JSZip();

//     // shp-write output
//     const out = shpwrite.write(geojson);

//     zip.file(`${fileName}.shp`, out.shp);
//     zip.file(`${fileName}.shx`, out.shx);
//     zip.file(`${fileName}.dbf`, out.dbf);
//     zip.file(`${fileName}.prj`, createPrjFileContent());

//     const blob = await zip.generateAsync({ type: "blob" });

//     const url = URL.createObjectURL(blob);
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = `${fileName}.zip`;
//     a.click();
//     URL.revokeObjectURL(url);
// };


// PRJ File content for WGS84
// export const createPrjFileContent = () => `GEOGCS["WGS 84",
// DATUM["WGS_1984",
//     SPHEROID["WGS 84",6378137,298.257223563,
//         AUTHORITY["EPSG","7030"]],
//     AUTHORITY["EPSG","6326"]],
// PRIMEM["Greenwich",0,
//     AUTHORITY["EPSG","8901"]],
// UNIT["degree",0.0174532925199433,
//     AUTHORITY["EPSG","9122"]],
// AUTHORITY["EPSG","4326"]]
// `;

// // Convert OL features to GeoJSON safely
// export const convertFeaturesToGeoJSON = (features: any[]) => {
//     const converted: any[] = features
//         .map((feature: any) => {
//             const geom = feature.getGeometry();
//             if (!geom) return null;

//             let newGeom: any;
//             const type = geom.getType();

//             try {
//                 if (type === "Polygon") {
//                     newGeom = new Polygon(
//                         geom.getCoordinates().map((ring: any[]) =>
//                             ring.map((c) => transform(c, "EPSG:3857", "EPSG:4326"))
//                         )
//                     );
//                 } else if (type === "LineString") {
//                     newGeom = new LineString(
//                         geom.getCoordinates().map((c: any) =>
//                             transform(c, "EPSG:3857", "EPSG:4326")
//                         )
//                     );
//                 } else if (type === "Point") {
//                     newGeom = new Point(
//                         transform(geom.getCoordinates(), "EPSG:3857", "EPSG:4326")
//                     );
//                 } else {
//                     console.warn(`Unsupported geometry type: ${type}`);
//                     return null;
//                 }

//                 const f = new Feature(newGeom);

//                 // Copy properties
//                 const props = feature.getProperties();
//                 Object.keys(props).forEach((key) => {
//                     if (key !== "geometry") f.set(key, props[key]);
//                 });

//                 return f;
//             } catch (error) {
//                 console.error('Error converting geometry:', error);
//                 return null;
//             }
//         })
//         .filter(Boolean);

//     const format = new GeoJSON();
//     return format.writeFeatures(converted);
// };

// // Create SHP ZIP
// export const generateShapefileZip = async (geojson: any, fileName: string) => {
//     try {
//         const zip = new JSZip();

//         // Convert GeoJSON string to object for shp-write
//         const geojsonObj = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;

//         // shp-write output - using the UMD version
//         const out = await import('shp-write');

//         // Generate shapefile components
//         const shpData = out.write(geojsonObj);

//         zip.file(`${fileName}.shp`, shpData.shp.buffer);
//         zip.file(`${fileName}.shx`, shpData.shx.buffer);
//         zip.file(`${fileName}.dbf`, shpData.dbf.buffer);
//         zip.file(`${fileName}.prj`, createPrjFileContent());

//         const blob = await zip.generateAsync({ type: "blob" });

//         // Use file-saver for reliable download
//         saveAs(blob, `${fileName}.zip`);

//         return true;
//     } catch (error) {
//         console.error('Error generating shapefile ZIP:', error);
//         throw error;
//     }
// };

// // Alternative method using direct blob creation (more reliable)
// export const generateShapefileZipAlternative = async (geojson: any, fileName: string) => {
//     try {
//         const zip = new JSZip();

//         // Convert GeoJSON string to object
//         const geojsonObj = typeof geojson === 'string' ? JSON.parse(geojson) : geojson;

//         // Dynamically import shp-write
//         const shpWrite = await import('shp-write');

//         // Generate shapefile components
//         const result = shpWrite.write(geojsonObj);

//         // Add files to zip
//         zip.file(`${fileName}.shp`, result.shp);
//         zip.file(`${fileName}.shx`, result.shx);
//         zip.file(`${fileName}.dbf`, result.dbf);
//         zip.file(`${fileName}.prj`, createPrjFileContent());

//         // Generate and download
//         const content = await zip.generateAsync({ type: "blob" });
//         saveAs(content, `${fileName}.zip`);

//         return true;
//     } catch (error) {
//         console.error('Error in shapefile generation:', error);
//         throw error;
//     }
// };


// PRJ File content for WGS84
export const createPrjFileContent = () => `GEOGCS["WGS 84",
DATUM["WGS_1984",
    SPHEROID["WGS 84",6378137,298.257223563,
        AUTHORITY["EPSG","7030"]],
    AUTHORITY["EPSG","6326"]],
PRIMEM["Greenwich",0,
    AUTHORITY["EPSG","8901"]],
UNIT["degree",0.0174532925199433,
    AUTHORITY["EPSG","9122"]],
AUTHORITY["EPSG","4326"]]
`;

// Convert features to GeoJSON with coordinate transformation
export const convertFeaturesToGeoJSON = (features: any[]) => {
    const geoJson = {
        type: 'FeatureCollection' as const,
        features: features?.map((feature: any) => {
            const coordinates = feature?.coordinates?.coordinates;
            const geometryType = feature?.coordinates?.type;

            if (!coordinates || !geometryType) return null;

            // Transform coordinates from EPSG:3857 to EPSG:4326
            const transformCoordinates = (coords: any): any => {
                if (geometryType === 'Point') {
                    return transform(coords, 'EPSG:3857', 'EPSG:4326');
                } else if (geometryType === 'LineString') {
                    return coords?.map((c: number[]) => transform(c, 'EPSG:3857', 'EPSG:4326'));
                } else if (geometryType === 'Polygon') {
                    return coords?.map((ring: number[][]) =>
                        ring?.map((c: number[]) => transform(c, 'EPSG:3857', 'EPSG:4326'))
                    );
                } else if (geometryType === 'MultiPolygon') {
                    return coords?.map((polygon: number[][][]) =>
                        polygon?.map((ring: number[][]) =>
                            ring?.map((c: number[]) => transform(c, 'EPSG:3857', 'EPSG:4326'))
                        )
                    );
                }
                return coords;
            };

            const transformedCoords = transformCoordinates(coordinates);

            return {
                type: 'Feature' as const,
                geometry: {
                    type: geometryType,
                    coordinates: transformedCoords
                },
                properties: {
                    name: feature?.featureName,
                    featureRef: feature?.featureRef,
                    layerName: feature?.layerName,
                    area: feature?.area || '',
                    perimeter: feature?.perimeter || '',
                    createdAt: feature?.createdAt || '',
                    createdBy: feature?.createdBy || '',
                    mineId: feature?.mineId || '',
                    color: feature?.color || '',
                    bufferDistance: feature?.bufferDistance || '',
                }
            };
        }).filter(Boolean)
    };

    return geoJson;
};


// // API service function - handles the Buffer response
// export const downloadSHPLayer = async (geoJsonData: any) => {
//     try {
//         const response = await fetch('http://192.168.20.80:4000/exportTool', {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//             body: JSON.stringify(geoJsonData)
//         });

//         if (!response.ok) {
//             throw new Error(`Server responded with status: ${response.status}`);
//         }

//         // Parse the JSON response containing the Buffer
//         const result = await response.json();
//         return result;

//     } catch (error) {
//         console.error('Error calling exportTool API:', error);
//         throw error;
//     }
// };


// // FRONTEND ONLY - generates SHP zip and returns same structure backend gives
// export const downloadSHPLayer = async (geoJsonData: any) => {
//     try {
//         const response = await fetch('http://192.168.20.80:4000/exportTool', {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//             body: JSON.stringify(geoJsonData)
//         });

//         if (!response.ok) {
//             throw new Error(`Server responded with status: ${response.status}`);
//         }

//         // Parse the JSON response containing the Buffer
//         const result = await response.json();
//         return result;



// Client-side SHP export — generates a ZIP blob directly in the browser using @mapbox/shp-write
export const downloadSHPLayer = async (geoJsonData: any): Promise<Blob> => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const shpwrite = require('@mapbox/shp-write');
    const base64Zip: string = await shpwrite.zip(geoJsonData, {
        folder: 'export',
        outputType: 'base64',
        compression: 'DEFLATE'
    });
    const binaryStr = atob(base64Zip);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'application/zip' });
};




export {
    vectorData,
    encryptDynamicPath,
    convertDateToUTC,
    mapProfileDetailsToSchema,
    AuthUser,
    IsAuthorized,
    hasPrivilege,
    Tooltip,
    add530ToTime,
    baseURL,
    convertBytesTo,
    convertDataToBytes,
    convertDate,
    convertSecondsTo,
    convertTextCase,
    convertUTCtoIST,
    convertScientificToUTC,
    ellipsisWithToolTip,
    ellipsisWithToolTipId,
    getDate,
    getDateOnly,
    getDateUTCFormat,
    getDateVal,
    getMonth,
    getPrivileges,
    getUTCGteLteFromDate,
    getUTCTime,
    handleBookingStatus,
    noCaseSensitiveSortBy,
    profileTypeLabel,
    sortBy,
    sortByAsc,
    uniqBy,
    uniqueID,
    formatDateToYYYYMMDD,
    formatDateToDDMMMYYYY,
    droneServiceActions,
    capitalize,
    createSelectOption,
    Tooltips
};